import { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  TextInput,
} from "react-native";
import { useRouter } from "expo-router";
import { useAcquisition } from "../AcquisitionContext";
import { useDevice, useScreenDimensions } from "../../_layout";
import { useTranslation } from "react-i18next";
import LoginLeftSidebar from "../components/LoginLeftSidebar";
import LanguagePicker from "../components/LanguagePicker";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import BlackPng from "../../../assets/BlackPng.png";
import Colors from "../../../Resources/Color";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../Firestore/FirestoreConfig";

/**
 * ForgotPasswordView component
 * Web-only password reset page with gradient sidebar and main content area
 */
const ForgotPasswordView = () => {
  const router = useRouter();
  const { buildUrlWithParams } = useAcquisition();
  const { screenSize } = useScreenDimensions();
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  // Form state - lifted to parent to persist across screenSize changes
  const [returnToSignInHovered, setReturnToSignInHovered] = useState(false);
  const [continueButtonHovered, setContinueButtonHovered] = useState(false);
  const [resendButtonHovered, setResendButtonHovered] = useState(false);
  const [email, setEmail] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sendResetEmail = (trimmedEmail) => {
    setIsLoading(true);

    sendPasswordResetEmail(auth, trimmedEmail)
      .then(() => {
        setEmailSent(true);
        setError("");
        setIsLoading(false);
        setCountdown(60); // Start 60 second countdown
      })
      .catch((error) => {
        console.error("Password reset error:", error);
        let errorMessage = error.message;
        if (error.code === "auth/user-not-found") {
          errorMessage = t("userNotFound-string") || "User not found";
        } else if (error.code === "auth/invalid-email") {
          errorMessage = t("invalidEmail-string") || "Invalid email format";
        }
        setError(errorMessage);
        setIsLoading(false);
      });
  };

  const handleContinue = () => {
    setError("");

    const trimmedEmail = email.trim();

    if (trimmedEmail === "") {
      setError(t("emailRequired-string") || "Email is required");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(t("invalidEmail-string") || "Invalid email format");
      return;
    }

    sendResetEmail(trimmedEmail);
  };

  const handleResend = () => {
    const trimmedEmail = email.trim();
    sendResetEmail(trimmedEmail);
  };

  // Main content area (right side)
  const MainContent = (
    <View style={styles.mainContent}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.contentContainer}>
            {/* Logo */}
            <Image source={BlackPng} style={styles.logo} resizeMode="contain" />

            {!emailSent ? (
              <>
                {/* Heading */}
                <Text style={styles.headingText}>
                  {t("resetYourPassword-string") || "Reset your password"}
                </Text>

                {/* Description */}
                <Text style={styles.descriptionText}>
                  {t("resetPasswordDescription-string") ||
                    "Enter the email address associated with your account, and we'll send you a link to reset your password."}
                </Text>

                {/* Form */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleContinue();
                  }}
                  style={{ width: 280 }}
                >
                  <View style={styles.emailPasswordContainer}>
                    {/* Email Label */}
                    <Text style={[styles.inputLabel, { marginBottom: -8 }]}>
                      {t("email-string") || "Email"}
                    </Text>

                    <TextInput
                      style={[
                        styles.input,
                        emailFocused && styles.inputFocused,
                        screenSize === "small" && { fontSize: 16 },
                      ]}
                      placeholderTextColor={Colors.grayColor}
                      value={email}
                      onChangeText={(text) => {
                        setError("");
                        setEmail(text);
                      }}
                      onFocus={() => setEmailFocused(true)}
                      onBlur={() => setEmailFocused(false)}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      autoComplete="email"
                      maxLength={254}
                    />

                    {/* Error message */}
                    {error != "" && (
                      <Text style={styles.errorText}>{error}</Text>
                    )}

                    {/* Continue Button */}
                    <TouchableOpacity
                      style={[
                        styles.continueButton,
                        !email && styles.continueButtonDisabled,
                        continueButtonHovered && styles.continueButtonHovered,
                      ]}
                      onPress={handleContinue}
                      onMouseEnter={() =>
                        !isMobile && setContinueButtonHovered(true)
                      }
                      onMouseLeave={() => setContinueButtonHovered(false)}
                      disabled={!email || isLoading}
                    >
                      {isLoading ? (
                        <CustomActivityIndicator large={false} white={true} />
                      ) : (
                        <Text style={styles.continueButtonText}>
                          {t("continue-string") || "Continue"}
                        </Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </form>
              </>
            ) : (
              <>
                {/* Success View - Check your inbox */}
                <Text style={styles.headingText}>
                  {t("checkYourInbox-string") || "Check your inbox"}
                </Text>

                {/* Success Description */}
                <Text style={styles.descriptionText}>
                  {t("checkInboxDescription-string")?.replace(
                    "{email}",
                    email
                  ) ||
                    `Thanks! If ${email} matches an email address we have on file, then we've sent you an email containing further instructions for resetting your password.\n\nIf you haven't received an email in 5 minutes, check your spam, resend, or try a different email address.`}
                </Text>

                {/* Resend Button */}
                <View style={styles.emailPasswordContainer}>
                  <TouchableOpacity
                    style={[
                      styles.continueButton,
                      countdown > 0 && styles.continueButtonDisabled,
                      resendButtonHovered &&
                        countdown === 0 &&
                        styles.continueButtonHovered,
                    ]}
                    onPress={handleResend}
                    onMouseEnter={() =>
                      !isMobile && setResendButtonHovered(true)
                    }
                    onMouseLeave={() => setResendButtonHovered(false)}
                    disabled={countdown > 0 || isLoading}
                  >
                    {isLoading ? (
                      <CustomActivityIndicator large={false} white={true} />
                    ) : (
                      <Text style={styles.continueButtonText}>
                        {countdown > 0
                          ? `${t("resend-string") || "Resend"} (${countdown}s)`
                          : t("resend-string") || "Resend"}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* Return to Sign In link */}
            <View style={styles.returnToSignInContainer}>
              <TouchableOpacity
                onPress={() => router.replace(buildUrlWithParams("/login"))}
                onMouseEnter={() => !isMobile && setReturnToSignInHovered(true)}
                onMouseLeave={() => setReturnToSignInHovered(false)}
              >
                <Text
                  style={[
                    styles.returnToSignInLink,
                    returnToSignInHovered && { color: "#2E5E6E" },
                  ]}
                >
                  {t("returnToSignIn-string") || "Return to Sign In"}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>

        {/* Language Picker - bottom left */}
        <View style={styles.languagePickerContainer}>
          <LanguagePicker />
        </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {screenSize === "large" && <LoginLeftSidebar />}
      {MainContent}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: "100vh",
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
  },
  mainContent: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  contentContainer: {
    alignItems: "center",
    maxWidth: "100%",
  },
  logo: {
    width: 48,
    height: 48,
    marginBottom: 32,
  },
  headingText: {
    fontSize: 32,
    fontWeight: "600",
    color: "#000000",
    marginBottom: 16,
    textAlign: "center",
  },
  descriptionText: {
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    marginBottom: 40,
    textAlign: "center",
    maxWidth: 400,
    lineHeight: 20,
  },
  returnToSignInContainer: {
    marginTop: 16,
    alignItems: "center",
  },
  returnToSignInLink: {
    fontSize: 14,
    color: Colors.bubbleColor,
    fontWeight: "400",
  },
  languagePickerContainer: {
    position: "absolute",
    bottom: 12,
    left: 12,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderRadius: 10,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  emailPasswordContainer: {
    width: 280,
    gap: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  input: {
    width: "100%",
    height: 44,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderRadius: 14,
    paddingHorizontal: 16,
    fontSize: 14,
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  inputFocused: {
    borderColor: Colors.bubbleColor,
  },
  errorText: {
    fontSize: 13,
    color: Colors.redColor,
    textAlign: "center",
    marginTop: -8,
  },
  continueButton: {
    width: "100%",
    height: 44,
    backgroundColor: Colors.bubbleColor,
    borderRadius: 14,
    justifyContent: "center",
    alignItems: "center",
  },
  continueButtonDisabled: {
    opacity: 0.5,
  },
  continueButtonHovered: {
    backgroundColor: "#2E5E6E",
  },
  continueButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#FFFFFF",
  },
});

export default ForgotPasswordView;
